import { Component, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { SlideComponent } from './slide/slide.component';
import { SlideRouterService } from './slide-router.service';
import { trigger, transition, query, style, animate, group, animateChild } from '@angular/animations';
import {CLASS_FOCUS, CLASS_BLUR} from './classes';

const LEFT_KEY = 37;
const SPACE_KEY = 32;
const RIGHT_KEY = 39;
const UP_KEY = 38;
const DOWN_KEY = 40;
const ESC_KEY = 27;

const NICE_EASING = 'cubic-bezier(.35,0,.25,1)';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  animations: [
    trigger('slideAnimation', [
      transition(':enter, :leave', []),
      transition(':increment', [
        query('.' + CLASS_FOCUS, [
          style({ transform: 'translateX(100%)'})
        ]),
        query('.' + CLASS_BLUR, [
          animateChild(),
        ]),
        group([
          query('.' + CLASS_BLUR, [
            animate('500ms ' + NICE_EASING, style({ transform: 'translateX(-100%)'}))
          ]),
          query('.' + CLASS_FOCUS, [
            animate('500ms ' + NICE_EASING, style({ transform: 'translateX(0%)'})),
          ]),
        ]),
        query('.' + CLASS_FOCUS, [
          animateChild()
        ]),
      ]),
      transition(':decrement', [
        query('.' + CLASS_FOCUS, [
          style({ transform: 'translateX(-100%)'})
        ]),
        query('.' + CLASS_BLUR, [
          animateChild(),
        ]),
        group([
          query('.' + CLASS_BLUR, [
            animate('500ms ' + NICE_EASING, style({ transform: 'translateX(100%)'}))
          ]),
          query('.' + CLASS_FOCUS, [
            animate('500ms ' + NICE_EASING, style({ transform: 'translateX(0%)'})),
          ]),
        ]),
        query('.' + CLASS_FOCUS, [
          animateChild()
        ]),
      ]),
    ])
  ]
})
export class AppComponent {
  public element: any;
  public slideIndex = 0;

  @ViewChildren('slide')
  public slides: QueryList<SlideComponent>;
  private _keyboard = new Keyboard();

  constructor(element: ElementRef, private _slideRouter: SlideRouterService) {
    this._keyboard.bind(LEFT_KEY, () => this.pressLeft());
    this._keyboard.bind(DOWN_KEY, () => this.pressDown());
    this._keyboard.bind(SPACE_KEY, e => this.pressSpace(e.shiftKey ? true : false));
    this._keyboard.bind(RIGHT_KEY, () => this.pressRight());
    this._keyboard.bind(UP_KEY, () => this.pressUp());
    this._keyboard.bind(ESC_KEY, () => {
      if (hasActiveInput()) {
        unFocus();
      } else {
        this.next();
      }
    });

    this.element = element.nativeElement;
    document.body.addEventListener('click', e => this.clickOpenArea());

    this._slideRouter.onRouteChange((slideIndex: number, pointIndex: number, slide: SlideComponent) => {
      if (!slide.skipAnimation) {
        this.slideIndex = slideIndex;
      }
    });
  }

  onRouteChange(index: number) {
    this.slideIndex = index;
  }

  pressSpace(reverse: boolean) {
    if (hasActiveInput()) return;
    if (reverse) {
      this._slideRouter.previous();
    } else {
      this._slideRouter.next();
    }
  }

  pressLeft() {
    if (hasActiveInput()) return;
    this._slideRouter.left();
  }

  pressUp() {
    if (hasActiveInput()) return;
    this._slideRouter.up();
  }

  pressRight() {
    if (hasActiveInput()) return;
    this._slideRouter.right();
  }

  pressDown() {
    if (hasActiveInput()) return;
    this._slideRouter.down();
  }

  clickOpenArea() {
    if (hasActiveInput()) return;
    this._slideRouter.next();
  }

  previous(event?: any) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this._slideRouter.previous();
  }

  next(event?: any) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this._slideRouter.next();
  }

  ngAfterViewInit() {
    this.slides.forEach(slide => {
      this._slideRouter.registerSlide(slide);
    });
    this._slideRouter.handleCurrentUrl();
  }
}

class Keyboard {
  private _element = document.body;
  private _lookup: {[key: string]: (e: any) => any} = {};

  constructor() {
    this._element.addEventListener('keydown', e => this._handleKey(e));
  }

  bind(key: number, cb: (e: any) => any) {
    this._lookup[key.toString()] = cb;
  }

  private _handleKey(e: any) {
    const code = e.keyCode.toString();
    const keys = Object.keys(this._lookup);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (key == code) {
        this._lookup[key](e);
        break;
      }
    }
  }
}

function hasActiveInput() {
  const elm = document.activeElement;
  const type = elm ? elm.nodeName.toLowerCase() : '';
  switch (type) {
    case 'input':
    case 'textarea':
    case 'select':
    case 'option':
      return true;
  }
  return false;
}

function unFocus() {
  if (hasActiveInput()) {
    (document.activeElement as any).blur();
  }
}



// WEBPACK FOOTER //
// ./src/app/app.component.ts